import { useState } from "react";
import { IconFileCertificate, IconFileCode, IconFileTypePdf } from "@tabler/icons-react";
import { Group, IconAction } from "@/components/ui";
import { toastError } from "@/lib/toast";
import {
  descargarAcusePdf,
  descargarFacturaPdf,
  descargarXmlDeFactura,
  FacturaPdfNoDisponibleError,
  type Factura,
} from "../services/facturas.api";

type DescargaTipo = "xml" | "factura" | "acuse";

/**
 * Descargas de UNA factura (XML original · PDF de la factura · PDF del acuse de
 * validación) como CHIPS de color: XML neutro, factura PDF rojo, acuse azul.
 * Compartido por «Mis facturas» y la fila expandible de «Mis solicitudes de pago».
 */
export function AccionesDescargaFactura({
  factura: f,
  justify = "center",
  size = "md",
}: {
  factura: Factura;
  justify?: "center" | "flex-start" | "flex-end";
  size?: "sm" | "md";
}) {
  const [descargando, setDescargando] = useState<DescargaTipo | null>(null);
  const iconSize = size === "sm" ? 16 : 18;

  const descargar = async (tipo: DescargaTipo) => {
    setDescargando(tipo);
    try {
      if (tipo === "xml") await descargarXmlDeFactura(f);
      else if (tipo === "factura") await descargarFacturaPdf(f);
      else await descargarAcusePdf(f);
    } catch (e) {
      const mensaje =
        tipo === "xml"
          ? "El XML original ya no está disponible."
          : tipo === "factura"
            ? e instanceof FacturaPdfNoDisponibleError
              ? "Esta solicitud no tiene un PDF de factura cargado."
              : "No se pudo descargar el PDF de la factura."
            : "No se pudo generar el PDF del acuse.";
      toastError({ title: "No se pudo descargar", message: mensaje });
    } finally {
      setDescargando(null);
    }
  };

  return (
    <Group gap={6} justify={justify} wrap="nowrap">
      <IconAction
        label={`Descargar XML de ${f.uuid}`}
        size={size}
        tone="neutral"
        loading={descargando === "xml"}
        onClick={() => descargar("xml")}
      >
        <IconFileCode size={iconSize} stroke={1.9} />
      </IconAction>
      <IconAction
        label={`Descargar PDF de la factura ${f.uuid}`}
        size={size}
        tone="peligro"
        loading={descargando === "factura"}
        onClick={() => descargar("factura")}
      >
        <IconFileTypePdf size={iconSize} stroke={1.9} />
      </IconAction>
      <IconAction
        label={`Descargar acuse de validación (PDF) de ${f.uuid}`}
        size={size}
        tone="info"
        loading={descargando === "acuse"}
        onClick={() => descargar("acuse")}
      >
        <IconFileCertificate size={iconSize} stroke={1.9} />
      </IconAction>
    </Group>
  );
}
